import React, { Component, Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from "react-router-dom";
import {
  Home,
  Cart,
  Checkout,
  Compare,
  OrderTracking,
  OrderConfirmation,
  AboutUs,
  ContactUs,
  PageNotFound,
  Product,
  SearchResult,
  Shop,
  Retour,
  PolitiqueDeConfidentialite,  UploadImgs,
  Slide,
  Products,
  AnalyticsDashboard,
  UnderConstruction
} from '../Pages/index';
import FxRevealObserver from '../components/FxRevealObserver';
import FloatingButtons from '../components/Other/FloatingButtons';
import AnalyticsTracker from '../components/Analytics/AnalyticsTracker';
import { scrollWindowToTop } from '../utils/scrollToTop';
import { tiktokPage } from '../utils/tiktokPixel';
import { metaInitPixel, metaPageView } from '../utils/metaPixel';

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="fixed top-0 left-0 w-full h-screen flex flex-col justify-center items-center gap-8 bg-white z-50">
    <img src="/logo.png" alt="Kissarity" className="h-14 w-auto object-contain opacity-90" />
    <div className="animate-spin rounded-full h-12 w-12 border-2 border-pink border-t-transparent"></div>
  </div>
);

// Component to handle route changes (title, analytics, scroll)
const RouteHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    switch (pathname) {
      case '/':
        document.title = 'Accueil | Kissaria';
        break;
      case '/cart':
        document.title = 'Panier | Kissaria';
        break;
      case '/checkout':
        document.title = 'Commande | Kissaria';
        break;
      case '/under-construction':
        document.title = 'Maintenance | Kissaria';
        break;
      case '/shop':
        document.title = 'Boutique | Kissaria';
        break;
      case '/pages/contact':
        document.title = 'Contactez-Nous | Kissaria';
        break;
      case '/pages/about':
        document.title = 'À Propos de Nous | Kissaria';
        break;
      default:
        if (pathname.startsWith('/product/')) {
          document.title = 'Produit | Kissaria';
        } else {
          document.title = 'Kissaria';
        }
    }

    tiktokPage();
    metaInitPixel();
    metaPageView();
    scrollWindowToTop();
  }, [location]);

  return null;
};

// Error boundary component
class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('Navigation Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              Please try refreshing the page or going back to the homepage.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.href = '/';
              }}
              className="bg-pink text-white px-6 py-2 rounded-lg hover:bg-pink-dark transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

class Navigation extends Component {
  componentDidMount() {
    window.addEventListener('popstate', this.handlePopState);

    if (!document.title) {
      document.title = "Kissaria";
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  handlePopState = (event: PopStateEvent) => {
    this.forceUpdate();

    setTimeout(() => {
      if (document.readyState === 'complete') {
        scrollWindowToTop();
      }
    }, 100);
  };

  render() {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <RouteHandler />
            <AnalyticsTracker />
            <FxRevealObserver />
            <Routes>
              <Route path='/' element={<Home />} />
              <Route path='/cart' element={<Cart />} />
              <Route path='/checkout' element={<Checkout />} />
              <Route path='/compare' element={<Compare />} />
              <Route path='/order-tracking' element={<OrderTracking />} />
              <Route path='/order-confirmation' element={<OrderConfirmation />} />
              <Route path='/pages/about' element={<AboutUs />} />
              <Route path='/pages/contact' element={<ContactUs />} />
              <Route path='/secret-page/dashboard-admin-200' element={<UploadImgs/>} />
              <Route path='/secret-page/dashboard-admin-200/slide' element={<Slide/>} />
              <Route path='/secret-page/dashboard-admin-200/products' element={<Products/>} />
              <Route path='/secret-page/dashboard-admin-200/analytics' element={<AnalyticsDashboard/>} />

              <Route path='/pages/livraison-et-retour' element={<Retour />} />
              <Route path='/pages/politique-de-confidentialite' element={<PolitiqueDeConfidentialite />} />

              <Route path='/product' element={<Product />} />
              <Route path='/product/:id' element={<Product />} />
              <Route path='/search-result' element={<SearchResult />} />
              <Route path='/shop' element={<Shop />} />
              <Route path='/under-construction' element={<UnderConstruction />} />
              <Route path='*' element={<PageNotFound />} />
            </Routes>
            <FloatingButtons />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }
}

export default Navigation;